/**
 * ActionExecutor — Handles suggest_intervention commands from the vision
 * loop server. Shows the avatar with a contextual suggestion, manages
 * accept/decline/ignore responses.
 *
 * Flow:
 *   1. Server sends suggest_intervention → avatar slides up with message
 *   2. User can: accept (click avatar), decline (click ✕), ignore (15s timeout)
 *   3. Result sent back to server via action_complete
 *   4. If accepted, triggers conversation start with vision context
 */

import type { VisionWSClient, InterventionMessage } from './wsClient'
import type { AvatarRenderer } from '../rendering/avatarRenderer'
import type { SpeechBubble } from '../rendering/speechBubble'
import { DismissButton } from '../rendering/dismissButton'
import type { Ticker } from 'pixi.js'

type WidgetStateGetter = () => string

const AUTO_DISMISS_MS = 15_000

export class ActionExecutor {
  private wsClient: VisionWSClient
  private avatar: AvatarRenderer
  private bubble: SpeechBubble
  private ticker: Ticker
  private dismissButton: DismissButton
  private getWidgetState: WidgetStateGetter

  private currentIntervention: InterventionMessage | null = null
  private autoDismissTimer: ReturnType<typeof setTimeout> | null = null
  private acceptHandler: (() => void) | null = null
  private _active = false

  /** Called when the user accepts a suggestion. Passes the rolling summary for conversation context. */
  onAccept: ((context: string) => void) | null = null

  constructor(opts: {
    wsClient: VisionWSClient
    avatar: AvatarRenderer
    bubble: SpeechBubble
    ticker: Ticker
    getWidgetState: WidgetStateGetter
  }) {
    this.wsClient = opts.wsClient
    this.avatar = opts.avatar
    this.bubble = opts.bubble
    this.ticker = opts.ticker
    this.getWidgetState = opts.getWidgetState
    this.dismissButton = new DismissButton()

    // Wire dismiss button
    this.dismissButton.onClick = () => this.handleDecline()

    // Listen for intervention commands
    this.wsClient.onIntervention = (msg) => this.handleIntervention(msg)
  }

  get isActive(): boolean {
    return this._active
  }

  /** Clean up. */
  destroy(): void {
    this.cleanup()
    this.wsClient.onIntervention = null
  }

  // -----------------------------------------------------------------------
  // Private — intervention handling
  // -----------------------------------------------------------------------

  private handleIntervention(msg: InterventionMessage): void {
    // Don't show interventions during active conversation or connecting
    const state = this.getWidgetState()
    if (state === 'active' || state === 'connecting') {
      console.log(`[ActionExecutor] Suppressed intervention — widget state is ${state}`)
      this.wsClient.sendActionComplete(msg.actionId, 'ignored')
      return
    }

    // If already showing a suggestion, ignore
    if (this._active) return

    console.log(`[ActionExecutor] Showing suggestion: "${msg.message}"`)
    this.currentIntervention = msg
    this._active = true

    // 1. Show avatar with static message in speech bubble
    this.avatar.slideIn(this.ticker)
    this.bubble.showStatic(msg.message)

    // 2. Show dismiss button
    this.dismissButton.show()

    // 3. Set up accept handler (click on avatar or bubble)
    this.acceptHandler = () => this.handleAccept()
    this.avatar.container.on('pointertap', this.acceptHandler)

    // 4. Start auto-dismiss timer
    this.autoDismissTimer = setTimeout(() => this.handleIgnore(), AUTO_DISMISS_MS)
  }

  private handleAccept(): void {
    if (!this._active || !this.currentIntervention) return
    console.log('[ActionExecutor] User accepted suggestion')

    const intervention = this.currentIntervention
    const context = `${intervention.context}\n\nThe user accepted your suggestion: "${intervention.message}"\nContinue the conversation naturally from this context.`
    const actionId = intervention.actionId

    this.cleanup()
    this.wsClient.sendActionComplete(actionId, 'accepted')
    this.onAccept?.(context)
  }

  private handleDecline(): void {
    if (!this._active || !this.currentIntervention) return
    console.log('[ActionExecutor] User declined suggestion')

    const actionId = this.currentIntervention.actionId
    this.cleanup()
    this.wsClient.sendActionComplete(actionId, 'declined')

    // Only slide avatar down if not in a conversation
    const state = this.getWidgetState()
    if (state !== 'active' && state !== 'connecting') {
      this.avatar.slideOut(this.ticker)
    }
  }

  private handleIgnore(): void {
    if (!this._active || !this.currentIntervention) return
    console.log('[ActionExecutor] Suggestion auto-dismissed (ignored)')

    const actionId = this.currentIntervention.actionId
    this.cleanup()
    this.wsClient.sendActionComplete(actionId, 'ignored')

    // Only slide avatar down if not in a conversation
    const state = this.getWidgetState()
    if (state !== 'active' && state !== 'connecting') {
      this.avatar.slideOut(this.ticker)
    }
  }

  // -----------------------------------------------------------------------
  // Private — cleanup
  // -----------------------------------------------------------------------

  private cleanup(): void {
    this._active = false

    // Clear auto-dismiss timer
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer)
      this.autoDismissTimer = null
    }

    // Hide dismiss button
    this.dismissButton.hide()

    // Hide speech bubble
    this.bubble.hideStatic()

    // Remove accept handler from avatar
    if (this.acceptHandler) {
      this.avatar.container.off('pointertap', this.acceptHandler)
      this.acceptHandler = null
    }

    this.currentIntervention = null
  }
}
